<template>
  <div class="form">
    <h1 class="form__title">{{ t('auth.login.title') }}</h1>
    <p class="form__text">{{ t('auth.login.register') }} <router-link :to="translations.i18nRoute({ name: 'auth-register' })">{{ t('buttons.signUp') }}</router-link></p>
    <form @submit.prevent="login" class="form__form">
      <ui-input
          :type="'email'"
          :placeholder="t('fields.email')"
          :rules="[isEmail]"
          v-model="email"
      />
      <ui-input
          :type="'password'"
          :placeholder="t('fields.password')"
          :rules="[required]"
          v-model="password"
      />
      <div class="form__bottom">
        <ui-checkbox
          v-model="isRemember"
        >
          {{ t('checkboxes.remember') }}
        </ui-checkbox>
        <router-link :to="translations.i18nRoute({ name: 'auth-reset' })" class="form__forgot">{{ t('auth.login.forgot') }}</router-link>
      </div>
      <ui-button
          :type="'submit'"
          class="form__button"
          :isDisabled="!isFormValid"
          :isLoading="isLoading"
      >
        {{ t('buttons.signIn') }}
      </ui-button>
    </form>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import translations from "@/core/helpers/translations.js";
import {isEmail, required} from "@/core/rules/textFieldRules.js";
import UiInput from "@/components/ui/ui-input.vue";
import {computed, ref} from "vue";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import UiButton from "@/components/ui/ui-button.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";
import {Orders} from "@/core/api/orders.js";
import {Wishlist} from "@/core/api/wishlist.js";
import {useAuthStore} from "@/stores/auth.js";
import {useCartStore} from "@/stores/cart.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {useRouter} from "vue-router";

const {t} = useI18n()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const router = useRouter()

const email = ref('')
const password = ref('')
const isRemember = ref(false)

const isLoading = ref(false)

const isFormValid = computed(() => {
  return (
      isEmail(email.value) === true &&
      required(password.value) === true
  )
})

const login = async () => {
  isLoading.value = true

  const res = await Auth.login(email.value, password.value)

  if (!res.errors) {
    authStore.setUser({
      user: res.data.obtainJwtToken.user,
      accessToken: res.data.obtainJwtToken.accessToken
    })

    await router.push({
      name: 'home',
      params: {
        locale: localStorage.getItem('awe-user-locale') || 'en-gb'
      }
    })

    localStorage.setItem('awe-refresh', res.data.obtainJwtToken.refreshToken)

    const currentOrder = await Orders.getOrders("PENDING", res.data.obtainJwtToken.accessToken, res.data.obtainJwtToken.user.email)
    cartStore.setCurrentOrders(currentOrder.data.orders.edges[0].node)

    const wishlistRes = await Wishlist.getWishlist(res.data.obtainJwtToken.accessToken)
    wishlistStore.wishlist = wishlistRes.data.wishlists.edges[0].node
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  &__title {
    color: #F3F3F3;
    text-align: center;
    font-size: 64px;
    font-weight: 700;
  }

  &__text {
    margin-top: 15px;
    color: $white;
    text-align: center;
    font-size: 20px;

    & a {
      transition: 0.2s;
      font-weight: 700;

      @include hover {
        color: #b7b7b7;
      }
    }
  }

  &__form {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  &__box {
    display: flex;
    align-items: flex-start;
    gap: 15px;
  }

  &__bottom {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__forgot {
    transition: 0.2s;
    color: $white;
    font-size: 16px;

    @include hover {
      color: #b7b7b7;
    }
  }

  &__link {
    text-decoration: underline;
  }

  &__button {
    margin-top: 10px;
    width: fit-content !important;
    padding: 8px 40px;
  }
}
</style>